import { OMNI_PREVIEW_TOKEN_KEY } from '../constants/preview'
import { log } from '../logs'

/**
 * 从 URL 中提取 omni_preview_token 参数
 * @param search - URL 的 search 参数字符串
 * @returns 提取到的 token 值，如果不存在则返回 undefined
 */
function extractTokenFromSearch(search: string): string | undefined {
  const params = new URLSearchParams(search)
  return params.get(OMNI_PREVIEW_TOKEN_KEY) || undefined
}

/**
 * 从 URL 的 hash 中提取 omni_preview_token 参数
 * @param hash - URL 的 hash 字符串
 * @returns 提取到的 token 值，如果不存在则返回 undefined
 */
function extractTokenFromHash(hash: string): string | undefined {
  // 移除 # 符号
  const hashWithoutPrefix = hash.startsWith('#') ? hash.slice(1) : hash
  // 如果 hash 中包含 ?，则提取查询参数部分
  const queryStart = hashWithoutPrefix.indexOf('?')
  const queryString = queryStart !== -1 ? hashWithoutPrefix.slice(queryStart + 1) : hashWithoutPrefix

  const params = new URLSearchParams(queryString)
  return params.get(OMNI_PREVIEW_TOKEN_KEY) || undefined
}

/**
 * 从 localStorage 中读取 omni_preview_token
 * @returns 存储的 token 值，如果不存在或读取失败则返回 undefined
 */
function getTokenFromLocalStorage(): string | undefined {
  try {
    const token = localStorage.getItem(OMNI_PREVIEW_TOKEN_KEY)
    return token || undefined
  } catch (error) {
    // localStorage 不可用（如隐私模式），优雅降级
    return undefined
  }
}

/**
 * 将 omni_preview_token 存储到 localStorage
 * @param token - 要存储的 token 值
 */
function setTokenToLocalStorage(token: string): void {
  try {
    localStorage.setItem(OMNI_PREVIEW_TOKEN_KEY, token)
  } catch (error) {
    // localStorage 不可用（如隐私模式），优雅降级
    // 不抛出错误，不影响 SDK 正常运行
    log.error('localStorage 不可用，无法存储 omni_preview_token')
  }
}

/**
 * 从 URL（包括 search 和 hash 参数）中提取 omni_preview_token
 * 如果 URL 中存在 token 则更新 localStorage，否则从 localStorage 读取 token
 *
 * @returns omni_preview_token 的值，如果不存在则返回 undefined
 *
 * @remarks
 * 支持以下 URL 格式：
 * - `https://example.com/?omni_preview_token=xxx` (search 参数)
 * - `https://example.com/#/?omni_preview_token=xxx` (hash 参数)
 * - `https://example.com/#preview?omni_preview_token=xxx` (hash 参数)
 *
 * 优先级：URL search > URL hash > localStorage
 *
 * @example
 * ```typescript
 * const token = getOmniPreviewToken()
 * if (token) {
 *   console.log('预览模式已启用，token:', token)
 * }
 * ```
 */
export const getOmniPreviewToken = (): string | undefined => {
  try {
    const token = extractTokenFromSearch(window.location.search) || extractTokenFromHash(window.location.hash)
    if (token !== undefined) {
      setTokenToLocalStorage(token)
    }
    return getTokenFromLocalStorage()
  } catch (error) {
    log.error('getOmniPreviewToken: 提取 token 时发生错误', error)
    return undefined
  }
}
